import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { z } from 'zod';
import { GROUP_PASS_MARKER, type GroupMember, type GroupSnapshot } from './contracts';

/**
 * Where a member's own conversation marks the turns the group gave it: the prompt it was told
 * (`prompt`, with the plain messages it heard) and its answer (`reply`). The prompt is protocol
 * for the agent, never the user's words: transcripts show a compact marker instead, and a reply
 * that passes is not shown at all.
 */
export const GROUP_TURN_METADATA_KEY = 'crowdex/group-turn';

const groupTurnMetadataSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('prompt'),
    /** The group's conversation. */
    group: z.string(),
    /** What was said in the group since the member last spoke. */
    messages: z.array(z.object({ authorId: z.string(), authorName: z.string(), text: z.string() })),
  }),
  z.object({ kind: z.literal('reply'), group: z.string() }),
]);
export type GroupTurnMetadata = z.infer<typeof groupTurnMetadataSchema>;

/** What was said in the group since the member last spoke, by the others. */
export function heardSince(
  member: GroupMember,
  snapshot: GroupSnapshot,
): { authorId: string; authorName: string; text: string }[] {
  const heard = snapshot.cursors[member.id] ?? 0;
  return snapshot.messages
    .filter((message) => message.sequence > heard && message.authorId !== member.id)
    .map((message) => ({
      authorId: message.authorId,
      authorName: message.authorId === 'user' ? 'User' : message.authorName,
      text: message.text,
    }));
}

/** The metadata of the prompt a member is told at its turn, and of its answer. */
export function groupTurnMetadataOf(
  member: GroupMember,
  snapshot: GroupSnapshot,
): { prompt: Record<string, GroupTurnMetadata>; reply: Record<string, GroupTurnMetadata> } {
  const group = snapshot.conversationId;
  return {
    prompt: {
      [GROUP_TURN_METADATA_KEY]: { kind: 'prompt', group, messages: heardSince(member, snapshot) },
    },
    reply: { [GROUP_TURN_METADATA_KEY]: { kind: 'reply', group } },
  };
}

/** The group turn a message of a member's conversation belongs to; undefined for others. */
export function groupTurnMetadata(
  message: Pick<CrowdexUIMessage, 'metadata'>,
): GroupTurnMetadata | undefined {
  const metadata = message.metadata as Record<string, unknown> | null | undefined;
  return groupTurnMetadataSchema.safeParse(metadata?.[GROUP_TURN_METADATA_KEY]).data;
}

/**
 * Whether a member's answer to the group passes (it had nothing to add): its words are only
 * the pass marker, or, while it streams, the start of it.
 */
export function isGroupPassReply(message: Pick<CrowdexUIMessage, 'metadata' | 'parts'>): boolean {
  if (groupTurnMetadata(message)?.kind !== 'reply') return false;
  const text = message.parts
    .flatMap((part) => (part.type === 'text' ? [part.text] : []))
    .join('')
    .trim();
  if (text === '') return false;
  return text.replaceAll(GROUP_PASS_MARKER, '').trim() === '' || GROUP_PASS_MARKER.startsWith(text);
}
